// Client Portal — Admin → Locations & People
//
// Editor for the reference data behind FieldIQ's Lookup tab: vote centers and
// drop boxes, the candidates list, and current officeholders.
//
// Two things shape this screen and are worth reading before changing it.
//
// 1. None of this data is facility-scoped. public.candidates has no
//    facility_id and the geo.* tables are keyed by state/district/county, so a
//    row edited here changes what every facility sees. The backend only lets
//    platform admins write; can_write comes back on the list response and this
//    panel renders read-only without it, rather than offering controls that
//    would 403.
//
// 2. Four of the five datasets are refreshed by loader scripts. Migration 161
//    gives them edited_fields so a hand-set column survives, and the ✎ marker
//    below is that column made visible — without it there is no way to tell a
//    value you corrected from one the source happens to agree with.

import { useCallback, useEffect, useState } from "react";
import { authFetch } from "../lib/api";
import {
  AlertTriangle,
  CheckCircle,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Trash2,
  X,
} from "lucide-react";

const DATASETS = [
  { id: "vote_centers", label: "Vote Centers" },
  { id: "candidates", label: "Candidates" },
  { id: "legislators", label: "Legislators" },
  { id: "executives", label: "Executives" },
  { id: "school_board", label: "School Board" },
];

// Columns worth showing in the grid, per dataset. Everything in `editable`
// from the API is still reachable in the row editor — this is only what earns
// a column at a glance.
const COLUMNS = {
  vote_centers: ["name", "loc_type", "city", "hours", "address"],
  candidates: ["full_name", "office", "party", "election_date", "active"],
  legislators: ["name", "chamber", "district", "party", "phone"],
  executives: ["name", "role", "level", "party", "phone"],
  school_board: ["name", "seat", "email", "phone"],
};

const FILTERS = {
  vote_centers: [
    {
      key: "loc_type",
      label: "All types",
      options: [
        ["vote_center", "Vote center"],
        ["drop_box", "Drop box"],
        ["drop_box_24h", "Drop box (24h)"],
      ],
    },
  ],
  legislators: [
    {
      key: "chamber",
      label: "Both chambers",
      options: [
        ["upper", "Senate"],
        ["lower", "House"],
      ],
    },
  ],
  executives: [
    {
      key: "level",
      label: "All levels",
      options: [
        ["state", "State"],
        ["federal", "Federal"],
      ],
    },
  ],
};

const titleCase = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const cellText = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

export const ReferenceDataPanel = ({ facility }) => {
  const fid = facility.facility_id;
  const [dataset, setDataset] = useState("vote_centers");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ editable: [], synced: false });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [editedOnly, setEditedOnly] = useState(false);
  const [editing, setEditing] = useState(null); // row being edited, or "new"
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const flash = (text, err = false) => {
    setMsg({ text, err });
    setTimeout(() => setMsg(null), 5000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ facility_id: fid });
    if (q.trim()) params.set("q", q.trim());
    if (editedOnly) params.set("edited_only", "true");
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    const data = await authFetch(`/admin/refdata/${dataset}?${params}`);
    if (data && !data.error) {
      setRows(data.rows || []);
      setMeta(data);
    } else {
      setRows([]);
      if (data?.error) flash(data.error, true);
    }
    setLoading(false);
  }, [fid, dataset, q, editedOnly, filters]); // eslint-disable-line

  // Switching dataset must clear filters — `chamber` means nothing to vote
  // centers and the backend would reject it.
  useEffect(() => {
    setFilters({});
    setQ("");
    setEditedOnly(false);
  }, [dataset]);

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0); // debounce typing only
    return () => clearTimeout(t);
  }, [load, q]);

  const isEdited = (row, field) => (row.edited_fields || []).includes(field);

  // Creating also needs any required field that is not editable afterwards —
  // school_board.district_geoid keys the row to a district, so it is set once
  // at creation and never changed. Without this the add form omits it and the
  // save is rejected for a field the user was never shown.
  const formFields = (creating) =>
    creating
      ? [
          ...meta.editable,
          ...(meta.required || []).filter((f) => !meta.editable.includes(f)),
        ]
      : meta.editable;

  const openEditor = (row) => {
    setEditing(row);
    const d = {};
    formFields(row === "new").forEach((f) => {
      d[f] = row === "new" ? "" : (row[f] ?? "");
    });
    setDraft(d);
  };

  const save = async () => {
    setSaving(true);
    const creating = editing === "new";
    // Only send what actually changed. Sending everything would mark every
    // field as hand-edited and freeze the whole row against its source.
    const body = creating
      ? draft
      : Object.fromEntries(
          Object.entries(draft).filter(
            ([k, v]) => String(v ?? "") !== String(editing[k] ?? ""),
          ),
        );
    if (!creating && !Object.keys(body).length) {
      setEditing(null);
      setSaving(false);
      return;
    }
    const url = creating
      ? `/admin/refdata/${dataset}?facility_id=${encodeURIComponent(fid)}`
      : `/admin/refdata/${dataset}/${encodeURIComponent(editing._pk)}?facility_id=${encodeURIComponent(fid)}`;
    const res = await authFetch(url, {
      method: creating ? "POST" : "PATCH",
      body: JSON.stringify(body),
    });
    setSaving(false);
    if (res?.error) {
      flash(res.error, true);
      return;
    }
    setEditing(null);
    flash(
      creating
        ? "Added."
        : meta.synced
          ? `Saved. ${Object.keys(body).length} field${Object.keys(body).length === 1 ? "" : "s"} now kept through the next sync.`
          : "Saved.",
    );
    load();
  };

  const revert = async (row, field) => {
    const res = await authFetch(
      `/admin/refdata/${dataset}/${encodeURIComponent(row._pk)}/revert?facility_id=${encodeURIComponent(fid)}`,
      { method: "POST", body: JSON.stringify({ field }) },
    );
    if (res?.error) {
      flash(res.error, true);
      return;
    }
    flash(res.note || "Reverted to source.");
    load();
  };

  const doDelete = async () => {
    const row = confirmDelete;
    const res = await authFetch(
      `/admin/refdata/${dataset}/${encodeURIComponent(row._pk)}?facility_id=${encodeURIComponent(fid)}`,
      { method: "DELETE" },
    );
    setConfirmDelete(null);
    if (res?.error) {
      flash(res.error, true);
      return;
    }
    flash("Deleted.");
    load();
  };

  const cols = COLUMNS[dataset] || [];
  const canWrite = Boolean(meta.can_write);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <MapPin className="w-5 h-5 text-teal-600" />
        <div className="flex-1">
          <h2 className="text-base font-semibold text-slate-800">
            Locations &amp; People
          </h2>
          <p className="text-sm text-slate-500">
            The reference data behind FieldIQ&rsquo;s Lookup tab.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="p-2 text-slate-400 hover:text-slate-600 rounded"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {!canWrite && (
        <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            Read-only. This data is shared by every facility on the platform, so
            only a platform admin can change it.
          </span>
        </div>
      )}

      {msg && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm border ${
            msg.err
              ? "bg-red-50 border-red-200 text-red-700"
              : "bg-green-50 border-green-200 text-green-700"
          }`}
        >
          {msg.err ? (
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          ) : (
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
          )}
          {msg.text}
        </div>
      )}

      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 overflow-x-auto">
        {DATASETS.map((d) => (
          <button
            key={d.id}
            onClick={() => setDataset(d.id)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap ${
              dataset === d.id
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-lg text-sm"
          />
        </div>
        {(FILTERS[dataset] || []).map((f) => (
          <select
            key={f.key}
            value={filters[f.key] || ""}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, [f.key]: e.target.value }))
            }
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
          >
            <option value="">{f.label}</option>
            {f.options.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        ))}
        {meta.synced && (
          <label className="flex items-center gap-2 text-sm text-slate-600 px-2">
            <input
              type="checkbox"
              checked={editedOnly}
              onChange={(e) => setEditedOnly(e.target.checked)}
              className="rounded border-slate-300"
            />
            Edited only
          </label>
        )}
        {canWrite && meta.can_create && (
          <button
            onClick={() => openEditor("new")}
            className="px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                {cols.map((c) => (
                  <th key={c} className="text-left px-3 py-2 font-semibold">
                    {titleCase(c)}
                  </th>
                ))}
                <th className="px-3 py-2 w-20" />
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={cols.length + 1}
                    className="px-3 py-8 text-center text-slate-400"
                  >
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={cols.length + 1}
                    className="px-3 py-8 text-center text-slate-400"
                  >
                    {editedOnly
                      ? "Nothing has been edited in this dataset."
                      : "No rows match."}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row._pk}
                    className="border-t border-slate-100 hover:bg-slate-50"
                  >
                    {cols.map((c) => (
                      <td key={c} className="px-3 py-2 text-slate-700">
                        <span className="inline-flex items-center gap-1">
                          {cellText(row[c])}
                          {isEdited(row, c) && (
                            <button
                              onClick={() => revert(row, c)}
                              title="Hand-edited — click to revert to source"
                              className="text-amber-500 hover:text-amber-700"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {canWrite && (
                        <>
                          <button
                            onClick={() => openEditor(row)}
                            className="px-2 py-1 text-xs text-blue-600 hover:text-blue-700"
                          >
                            Edit
                          </button>
                          {meta.can_delete && (
                            <button
                              onClick={() => setConfirmDelete(row)}
                              className="px-1 py-1 text-slate-400 hover:text-rose-600"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {rows.length >= 500 && (
          <p className="px-3 py-2 text-xs text-slate-400 border-t border-slate-100">
            Showing the first 500 — narrow the search to see the rest.
          </p>
        )}
      </div>

      {/* Row editor */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 sticky top-0 bg-white">
              <h3 className="font-semibold text-slate-800">
                {editing === "new" ? "Add row" : "Edit row"}
              </h3>
              <button
                onClick={() => setEditing(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {meta.synced && editing !== "new" && (
                <p className="text-xs text-slate-500">
                  This dataset is refreshed from a source. A field you change
                  here is kept through future syncs until you revert it.
                </p>
              )}
              {formFields(editing === "new").map((f) => (
                <div key={f}>
                  <label className="flex items-center justify-between text-xs font-medium text-slate-600 mb-1">
                    <span>{titleCase(f)}</span>
                    {editing !== "new" && isEdited(editing, f) && (
                      <button
                        onClick={() => {
                          revert(editing, f);
                          setEditing(null);
                        }}
                        className="flex items-center gap-1 text-amber-600 hover:text-amber-700"
                      >
                        <RotateCcw className="w-3 h-3" /> revert to source
                      </button>
                    )}
                  </label>
                  {typeof draft[f] === "boolean" ||
                  typeof editing?.[f] === "boolean" ? (
                    <select
                      value={String(draft[f])}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          [f]: e.target.value === "true",
                        }))
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : (
                    <input
                      value={draft[f] ?? ""}
                      onChange={(e) =>
                        setDraft((d) => ({ ...d, [f]: e.target.value }))
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-3 px-5 py-3 border-t border-slate-100 sticky bottom-0 bg-white">
              <button
                onClick={() => setEditing(null)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h3 className="font-semibold text-slate-800">Delete this row?</h3>
            <p className="text-sm text-slate-600">
              {confirmDelete.name || confirmDelete.full_name}
            </p>
            <p className="text-xs text-slate-500">
              Every facility reads this data. If the row comes from a source
              feed, the next sync will re-create it.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={doDelete}
                className="flex-1 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
